import { FavouriteAdded, FavouriteRemoved } from '@studyportals/wishlist-service-core';
import { WishlistOrderChanged } from '@studyportals/wishlist-service-core/src/domain/events/wishlist-order-changed';
import {
	IEvent,
	IEventAggregationService,
	ISubscriber,
	SessionCreatedEvent,
	SessionDestroyedEvent,
	SessionServiceReadyEvent
} from '../../interfaces';
import { ITokenBasedSession, ITokenBasedSessionService } from '../../interfaces';
import { IWishlistService } from '../../interfaces';
import { LegacySessionEventType } from '../session-management/domain/enums/legacy-session-event-type';
import { WishlistEventType } from '../wishlist-service/domain/enums/wishlist-event-type';
import { GroupEventEmitter } from './group-event-emitter';
import { WebSocketGatewaySubscriber } from './web-socket-gateway-subscriber';
import { IWebSocketGateway } from './web-socket-gateway.interface';

export class EventAggregationService implements IEventAggregationService {

	private groupEventEmitter: GroupEventEmitter;

	constructor(
		private webSocketGateway: IWebSocketGateway
	) {
		this.groupEventEmitter = new GroupEventEmitter();
	}

	/**
	 * @deprecated please use subscribeTo
	 * */
	public subscribeToSessionCreatedEvent(subscriber: ISubscriber<SessionCreatedEvent>, replay?: boolean): void {
		this.subscribeTo(LegacySessionEventType.SESSION_CREATED, subscriber, replay)
	}

	/**
	 * @deprecated please use publishTo
	 * */
	public publishSessionCreatedEvent(session: ITokenBasedSession): void {
		//TODO: remove once subscribeTo is fully supported
		this.groupEventEmitter.notify(LegacySessionEventType.SESSION_CREATED, new SessionCreatedEvent(session));

		const event = new SessionCreatedEvent(session);
		this.publishTo(SessionCreatedEvent.EventType, event);
	}

	public subscribeToSessionRefreshedEvent(subscriber: ISubscriber<void>, replay?: boolean): void {
		this.groupEventEmitter.subscribe(LegacySessionEventType.SESSION_REFRESHED, subscriber, replay);
	}

	public publishSessionRefreshedEvent(): void {
		this.groupEventEmitter.notify(LegacySessionEventType.SESSION_REFRESHED);
	}

	public subscribeToSessionDestroyedEvent(subscriber: ISubscriber<void>, replay?: boolean): void {
		this.groupEventEmitter.subscribe(LegacySessionEventType.SESSION_DESTROYED, subscriber, replay);
	}

	/**
	 * @deprecated please use publishTo
	 * */
	public publishSessionDestroyedEvent(): void {
		//TODO: remove once subscribeTo is fully supported
		this.groupEventEmitter.notify(LegacySessionEventType.SESSION_DESTROYED);

		const event = new SessionDestroyedEvent();
		this.publishTo(SessionDestroyedEvent.EventType, event);
	}

	public subscribeToSessionServiceReadyEvent(subscriber: ISubscriber<ITokenBasedSessionService>, replay?: boolean): void {
		this.groupEventEmitter.subscribe(LegacySessionEventType.SESSION_SERVICE_READY, subscriber, replay);
	}

	/**
	 * @deprecated please use publishTo
	 * */
	public publishSessionServiceReadyEvent(sessionService: ITokenBasedSessionService): void {
		//TODO: remove once subscribeTo is fully supported
		this.groupEventEmitter.notify(LegacySessionEventType.SESSION_SERVICE_READY, sessionService);

		const event = new SessionServiceReadyEvent(sessionService);
		this.publishTo(SessionServiceReadyEvent.EventType, event);
	}

	public subscribeToSessionRepositoryReadyEvent(subscriber: ISubscriber<void>, replay?: boolean): void {
		this.groupEventEmitter.subscribe(LegacySessionEventType.SESSION_REPOSITORY_READY, subscriber, replay);
	}

	public publishSessionRepositoryReadyEvent(): void {
		this.groupEventEmitter.notify(LegacySessionEventType.SESSION_REPOSITORY_READY);
	}

	public subscribeToWishlistServiceReadyEvent(subscriber: ISubscriber<IWishlistService>, replay?: boolean): void {
		this.groupEventEmitter.subscribe(WishlistEventType.WISHLIST_SERVICE_READY, subscriber, replay);
	}

	public publishWishlistServiceReadyEvent(wishlistService: IWishlistService): void {
		this.groupEventEmitter.notify(WishlistEventType.WISHLIST_SERVICE_READY, wishlistService);
	}

	public subscribeToFavouriteAddedEvent(subscriber: ISubscriber<FavouriteAdded>, replay?: boolean): void {
		this.groupEventEmitter.subscribe(WishlistEventType.FAVOURITE_ADDED, subscriber, replay);
	}

	public publishFavouriteAddedEvent(event: FavouriteAdded): void {
		this.groupEventEmitter.notify(WishlistEventType.FAVOURITE_ADDED, event);
	}

	public subscribeToFavouriteRemovedEvent(subscriber: ISubscriber<FavouriteRemoved>, replay?: boolean): void {
		this.groupEventEmitter.subscribe(WishlistEventType.FAVOURITE_REMOVED, subscriber, replay);
	}

	public publishFavouriteRemovedEvent(event: FavouriteRemoved): void {
		this.groupEventEmitter.notify(WishlistEventType.FAVOURITE_REMOVED, event);
	}

	public subscribeToWishlistOrderChangedEvent(subscriber: ISubscriber<WishlistOrderChanged>, replay?: boolean): void {
		this.groupEventEmitter.subscribe(WishlistEventType.WISHLIST_ORDER_CHANGED, subscriber, replay);
	}

	public publishWishlistOrderChangedEvent(event: WishlistOrderChanged): void {
		this.groupEventEmitter.notify(WishlistEventType.WISHLIST_ORDER_CHANGED, event);
	}

	public subscribeToWebSocketEvent(eventType: string, subscriber: ISubscriber<any>): void {
		this.webSocketGateway.addSubscriber(new WebSocketGatewaySubscriber(eventType, subscriber));
	}

	public subscribeTo(eventType: string, subscriber: ISubscriber<IEvent>, replay?: boolean): void {
		this.groupEventEmitter.subscribe(eventType, subscriber, replay);
	}

	public publishTo(eventType: string, event: IEvent): void {
		this.groupEventEmitter.notify(eventType, event);
	}
}
